// src/types/Person.ts

// Person Status Options
export const PERSON_STATUSES = [
  'suspect',
  'lead',
  'prospect',
  'active',
  'inactive',
  'customer',
  'supplier',
] as const
export type PersonStatus = (typeof PERSON_STATUSES)[number]

// Engagement Stage Options
export const ENGAGEMENT_STAGES = [
  'uncontacted',
  'contacted',
  'qualified',
  'rejected',
  'former',
] as const
export type EngagementStage = (typeof ENGAGEMENT_STAGES)[number]

// Attachment Category Types
export type AttachmentCategory =
  | 'taxForm'
  | 'businessLicense'
  | 'certification'
  | 'contract'
  | 'other'

// Person Type Options
export type PersonType = 'supplier' | 'customer' | 'both'

// Person Source Options
export type PersonSource = 'manual' | 'imported' | 'form' | 'referral'

// Attachment Interface
export interface PersonAttachment {
  id: string
  name: string
  fileType: string
  url: string
  category: AttachmentCategory
  uploadedAt: Date
  expiresAt?: Date
  notes?: string
}

// Main Person Interface
export interface Person {
  id?: string

  // Names and Identity
  firstName: string
  lastName: string
  middleName?: string
  jobTitle?: string
  contactFor?: string
  organizationId?: string

  // Contact Info
  email?: string
  phone?: string
  mobilePhone?: string
  streetAddress?: string
  city?: string
  state?: string
  postalCode?: string
  country?: string

  // ID Details
  idNumber?: string
  photoIdUrl?: string
  photoIdType?: string
  photoIdExpirationDate?: Date
  barcodeHash?: string
  photoIdFile?: File
  photoIdPreview?: string
  dateOfBirth?: string
  currentIdDocumentId?: string

  // Additional Files
  attachments?: PersonAttachment[]

  // CRM + Relationship
  notes?: string
  referredBy?: string
  tags?: string[]
  status?: PersonStatus
  type?: PersonType
  engagementStage?: EngagementStage
  source?: PersonSource

  // Metrics
  rating?: number
  lastTransactionDate?: Date | null

  // Flags
  isVerified?: boolean
  isExempt?: boolean
  isDeleted?: boolean

  // Search Fields
  firstName_lower?: string
  lastName_lower?: string

  // Meta
  createdAt: Date
  updatedAt: Date
  createdBy?: string | null

  // Search Scopes
  yardId?: string
  userId?: string
}

// Helper type for UI display
export interface PersonWithOrgName extends Person {
  organizationName?: string
}

// UI Option Types
export const personStatusOptions = PERSON_STATUSES.map((value) => ({
  value,
  label: value.charAt(0).toUpperCase() + value.slice(1),
}))

export const engagementStageOptions = ENGAGEMENT_STAGES.map((value) => ({
  value,
  label: value.charAt(0).toUpperCase() + value.slice(1),
}))

export const personTypeOptions = [
  { label: 'Customer', value: 'customer' as PersonType },
  { label: 'Supplier', value: 'supplier' as PersonType },
  { label: 'Both', value: 'both' as PersonType },
]

export const personSourceOptions = [
  { label: 'Manual', value: 'manual' as PersonSource },
  { label: 'Imported', value: 'imported' as PersonSource },
  { label: 'Form', value: 'form' as PersonSource },
  { label: 'Referral', value: 'referral' as PersonSource },
]
